import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Stack,
} from '@mui/material';
import { useUpdate, Identifier } from 'react-admin';
import { QuoteLine, QuoteLineRate } from '../types';
import { supabase } from '../providers/supabase/supabase';
import { readSavedEndorsementRate } from '../hail-pricing-tool/endorsementRates';
import { stateAbbreviationMap } from '../utils/stateAbbreviations';

type DiscountLookup = {
    aip: string;
    state: string;
    has_discount: boolean;
    discount: number | null;
    discount_deadline: string | null;
    excluded_crops: string[];
    excluded_products: string[];
};

interface QuoteRatesDisplayProps {
    quoteLines: QuoteLine[];
    quoteId: Identifier;
    selectedHailProducts?: string[];
    paymentMethod?: string;
    state?: string;
    selectedEndorsements?: Record<string, string>;
    endorsementRateLookup?: Record<string, number>;
}

const carrierColors: Record<string, { bg: string; color: string }> = {
    RCIS: { bg: '#e6f3f0', color: '#4a9e8c' },
    NAU: { bg: '#e6f6e6', color: '#4a9e4a' },
    'Rain & Hail': { bg: '#e6e6f6', color: '#5b4a9e' },
    proAg: { bg: '#fef3e6', color: '#c77a1a' },
    'Pro Ag': { bg: '#fef3e6', color: '#c77a1a' },
};

const getCarrierColor = (carrier: string) =>
    carrierColors[carrier] ?? { bg: '#f5f5f5', color: '#666' };

/** Normalize carrier keys like 'proAg' to display names like 'Pro Ag'. */
const carrierDisplayName = (carrier: string): string => {
    if (carrier === 'proAg' || carrier === 'ProAg') return 'Pro Ag';
    return carrier;
};

const HIGHLIGHT_BG = '#e6f3f0';
const SELECTED_COLOR = '#4a9e8c';
const UNSELECTED_BORDER = '#bdbdbd';

// --- Formatting helpers ---

/** Parse rate to number, stripping $, %, and other non-numeric chars.
 *  Mirrors RatePdfDocument's getRateNum so the table matches the PDF. */
const parseRateNum = (rate: string | number | null): number => {
    if (rate == null) return NaN;
    if (typeof rate === 'number') return rate;
    const cleaned = String(rate).replace(/[^\d.-]/g, '');
    return parseFloat(cleaned);
};

const formatRate = (rate: string | number | null): string => {
    const num = parseRateNum(rate);
    if (isNaN(num)) return '—';
    return `${num.toFixed(2)}%`;
};

const formatCostPerAcre = (
    rate: string | number | null,
    ipa: number
): string => {
    const num = parseRateNum(rate);
    if (isNaN(num) || !ipa) return '—';
    const cost = ipa * (num / 100);
    return `$${cost.toFixed(2)}`;
};

const formatDiscRate = (
    rate: string | number | null,
    discountFraction: number
): string => {
    const num = parseRateNum(rate);
    if (isNaN(num)) return '—';
    if (discountFraction <= 0) return `${num.toFixed(2)}%`;
    const discRate = num * (1 - discountFraction);
    return `${discRate.toFixed(2)}%`;
};

const formatDiscCostPerAcre = (
    rate: string | number | null,
    ipa: number,
    discountFraction: number
): string => {
    const num = parseRateNum(rate);
    if (isNaN(num) || !ipa) return '—';
    const discRate = num * (1 - discountFraction);
    const cost = ipa * (discRate / 100);
    return `$${cost.toFixed(2)}`;
};

// --- Selection circle (radio/checkbox style) ---

const SelectionCircle = ({
    selected,
    onClick,
}: {
    selected: boolean;
    onClick: () => void;
}) => (
    <Box
        onClick={onClick}
        sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            border: `2px solid ${selected ? SELECTED_COLOR : UNSELECTED_BORDER}`,
            bgcolor: selected ? SELECTED_COLOR : 'transparent',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            flexShrink: 0,
            '&:hover': {
                borderColor: SELECTED_COLOR,
                bgcolor: selected ? '#3d8a7a' : 'rgba(74, 158, 140, 0.08)',
            },
        }}
    >
        {selected && (
            <Box
                component="span"
                sx={{
                    color: 'white',
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                }}
            >
                ✓
            </Box>
        )}
    </Box>
);

// --- Main component ---

export const QuoteRatesDisplay = ({
    quoteLines,
    quoteId,
    selectedHailProducts: allowedProducts,
    paymentMethod,
    state,
    selectedEndorsements,
    endorsementRateLookup,
}: QuoteRatesDisplayProps) => {
    const [update] = useUpdate();

    const showDiscount = paymentMethod === 'Cash Discount (when applicable)';
    const [discountData, setDiscountData] = useState<DiscountLookup[]>([]);

    useEffect(() => {
        if (!showDiscount || !state) {
            setDiscountData([]);
            return;
        }
        const abbr = stateAbbreviationMap[state];
        if (!abbr) {
            setDiscountData([]);
            return;
        }
        supabase
            .from('discount_manager')
            .select(
                'aip, state, has_discount, discount, discount_deadline, excluded_crops, excluded_products'
            )
            .eq('state', abbr)
            .then(({ data, error }) => {
                if (error) {
                    console.error('Error fetching discounts:', error);
                    setDiscountData([]);
                } else {
                    setDiscountData((data as DiscountLookup[]) ?? []);
                }
            });
    }, [showDiscount, state]);

    const discountMap = useMemo(() => {
        const map = new Map<string, number>();
        discountData.forEach(d => {
            if (d.has_discount && d.discount != null) {
                map.set(d.aip, d.discount);
                // Also map alternate carrier key forms so lookups work
                // e.g. "Pro Ag" -> also keyed as "proAg" / "ProAg"
                if (d.aip === 'Pro Ag') {
                    map.set('proAg', d.discount);
                    map.set('ProAg', d.discount);
                }
            }
        });
        return map;
    }, [discountData]);

    const discountChipMap = useMemo(() => {
        const map = new Map<string, { pct: string; due: string }>();
        discountData.forEach(d => {
            if (d.has_discount && d.discount != null) {
                const info = {
                    pct:
                        (d.discount * 100).toFixed(
                            Number.isInteger(d.discount * 100) ? 0 : 1
                        ) + '%',
                    due: d.discount_deadline || '',
                };
                map.set(d.aip, info);
                if (d.aip === 'Pro Ag') {
                    map.set('proAg', info);
                    map.set('ProAg', info);
                }
            }
        });
        return map;
    }, [discountData]);

    // Build a lookup from AIP -> excluded_crops list
    const exclusionMap = useMemo(() => {
        const map = new Map<string, string[]>();
        discountData.forEach(d => {
            if (d.excluded_crops?.length > 0) {
                map.set(d.aip, d.excluded_crops);
                if (d.aip === 'Pro Ag') {
                    map.set('proAg', d.excluded_crops);
                    map.set('ProAg', d.excluded_crops);
                }
            }
        });
        return map;
    }, [discountData]);

    // Build a lookup from AIP -> excluded_products list
    const productExclusionMap = useMemo(() => {
        const map = new Map<string, string[]>();
        discountData.forEach(d => {
            if (d.excluded_products?.length > 0) {
                map.set(d.aip, d.excluded_products);
                if (d.aip === 'Pro Ag') {
                    map.set('proAg', d.excluded_products);
                    map.set('ProAg', d.excluded_products);
                }
            }
        });
        return map;
    }, [discountData]);

    if (!quoteLines || quoteLines.length === 0) return null;

    const handleCarrierSelect = (lineIdx: number, carrier: string) => {
        const line = quoteLines[lineIdx];
        // Radio behavior: toggle off if already selected, else switch
        const newCarrier = line.selectedCarrier === carrier ? null : carrier;
        const updatedLines = quoteLines.map((l, i) =>
            i === lineIdx ? { ...l, selectedCarrier: newCarrier } : l
        );
        update('quotes', {
            id: quoteId,
            data: { quote_lines: updatedLines },
            previousData: { quote_lines: quoteLines },
        });
    };

    const handleProductToggle = (lineIdx: number, product: string) => {
        const line = quoteLines[lineIdx];
        const current = line.selectedProducts ?? [];
        // Checkbox behavior: toggle in/out of array
        const updated = current.includes(product)
            ? current.filter(p => p !== product)
            : [...current, product];
        const updatedLines = quoteLines.map((l, i) =>
            i === lineIdx ? { ...l, selectedProducts: updated } : l
        );
        update('quotes', {
            id: quoteId,
            data: { quote_lines: updatedLines },
            previousData: { quote_lines: quoteLines },
        });
    };

    return (
        <Stack spacing={3}>
            {quoteLines.map((line, lineIdx) => (
                <LineMatrix
                    key={line.id || lineIdx}
                    line={line}
                    lineIdx={lineIdx}
                    allowedProducts={allowedProducts}
                    onCarrierSelect={handleCarrierSelect}
                    onProductToggle={handleProductToggle}
                    showDiscount={showDiscount}
                    discountMap={discountMap}
                    discountChipMap={discountChipMap}
                    exclusionMap={exclusionMap}
                    productExclusionMap={productExclusionMap}
                    selectedEndorsements={selectedEndorsements}
                    endorsementRateLookup={endorsementRateLookup}
                />
            ))}
        </Stack>
    );
};

// --- Per-line matrix table ---

interface LineMatrixProps {
    line: QuoteLine;
    lineIdx: number;
    allowedProducts?: string[];
    onCarrierSelect: (lineIdx: number, carrier: string) => void;
    onProductToggle: (lineIdx: number, product: string) => void;
    showDiscount: boolean;
    discountMap: Map<string, number>;
    discountChipMap: Map<string, { pct: string; due: string }>;
    exclusionMap: Map<string, string[]>;
    productExclusionMap: Map<string, string[]>;
    selectedEndorsements?: Record<string, string>;
    endorsementRateLookup?: Record<string, number>;
}

const LineMatrix = ({
    line,
    lineIdx,
    allowedProducts,
    onCarrierSelect,
    onProductToggle,
    showDiscount,
    discountMap,
    discountChipMap,
    exclusionMap,
    productExclusionMap,
    selectedEndorsements,
    endorsementRateLookup,
}: LineMatrixProps) => {
    // Build unique ordered lists (preserving insertion order).
    // Use the same form-name strategy as the PDF renderer:
    // displayForm || originalForm, with no cross-carrier
    // remapping.  This keeps the table consistent with the PDF.
    const carriers: string[] = [];
    const allProducts: string[] = [];
    const rateMap = new Map<string, QuoteLineRate>();

    line.rates.forEach(r => {
        if (!carriers.includes(r.carrier)) carriers.push(r.carrier);
        const form = r.displayForm || r.originalForm || '';
        if (form && !allProducts.includes(form)) allProducts.push(form);
        rateMap.set(`${r.carrier}|${form}`, r);
    });

    // Filter to only products selected in the HPT quote
    const products =
        allowedProducts && allowedProducts.length > 0
            ? allProducts.filter(p => allowedProducts.includes(p))
            : allProducts;

    // Build endorsement entries: [type, name, shortLabel][]
    const endorsementEntries = useMemo(() => {
        if (!selectedEndorsements) return [];
        return Object.entries(selectedEndorsements).map(([type, name]) => ({
            type,
            name,
            label: name
                .replace('Wind Shatter ', 'WSH ')
                .replace('Wind Shatter', 'WSH'),
        }));
    }, [selectedEndorsements]);

    const hasEndorsements =
        endorsementEntries.length > 0 && !!endorsementRateLookup;

    const selectedCarrier = line.selectedCarrier ?? null;
    const selectedProducts = line.selectedProducts ?? [];

    const isHighlighted = (carrier: string, product: string) =>
        selectedCarrier === carrier && selectedProducts.includes(product);

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                {line.county} – {line.crop} – {line.township} – {line.range} -
                IPA ${line.ipa != null ? Number(line.ipa).toFixed(2) : '0.00'}
            </Typography>

            <TableContainer
                component={Paper}
                sx={{
                    boxShadow: 'none',
                    border: '1px solid #e0e0e0',
                    overflowX: 'auto',
                }}
            >
                <Table size="small" sx={{ minWidth: 400 }}>
                    <TableHead>
                        {/* Row 1: Product names + selection circles */}
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell
                                rowSpan={2}
                                sx={{
                                    fontWeight: 'bold',
                                    borderRight: '1px solid #e0e0e0',
                                    verticalAlign: 'bottom',
                                    position: 'sticky',
                                    left: 0,
                                    bgcolor: '#f5f5f5',
                                    zIndex: 2,
                                    minWidth: 130,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    AIP
                                    {showDiscount && (
                                        <Chip
                                            label="Discount | Due"
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                fontSize: '0.6rem',
                                                height: 18,
                                            }}
                                        />
                                    )}
                                </Box>
                            </TableCell>
                            {products.map(product => (
                                <TableCell
                                    key={product}
                                    colSpan={
                                        (showDiscount ? 3 : 2) +
                                        endorsementEntries.length
                                    }
                                    align="center"
                                    sx={{
                                        borderRight: '1px solid #e0e0e0',
                                        py: 1,
                                    }}
                                >
                                    <Stack alignItems="center" spacing={0.5}>
                                        <SelectionCircle
                                            selected={selectedProducts.includes(
                                                product
                                            )}
                                            onClick={() =>
                                                onProductToggle(
                                                    lineIdx,
                                                    product
                                                )
                                            }
                                        />
                                        <Typography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{
                                                fontSize: '0.8rem',
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {product}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                            ))}
                        </TableRow>

                        {/* Row 2: Rate / Disc. Rate / Cost/Acre sub-headers */}
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            {products.map(product => (
                                <React.Fragment key={product}>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            fontSize: '0.7rem',
                                            color: 'text.secondary',
                                            py: 0.5,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Rate
                                    </TableCell>
                                    {endorsementEntries.map(endo => (
                                        <TableCell
                                            key={endo.type}
                                            align="right"
                                            sx={{
                                                fontSize: '0.7rem',
                                                color: 'text.secondary',
                                                py: 0.5,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {endo.label}
                                        </TableCell>
                                    ))}
                                    {showDiscount && (
                                        <TableCell
                                            align="right"
                                            sx={{
                                                fontSize: '0.7rem',
                                                color: 'text.secondary',
                                                py: 0.5,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Disc. Rate
                                        </TableCell>
                                    )}
                                    <TableCell
                                        align="right"
                                        sx={{
                                            fontSize: '0.7rem',
                                            color: 'text.secondary',
                                            borderRight: '1px solid #e0e0e0',
                                            py: 0.5,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Cost/Acre
                                    </TableCell>
                                </React.Fragment>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {carriers.map((carrier, cidx) => {
                            const isCarrierSelected =
                                selectedCarrier === carrier;
                            const carrierColor = getCarrierColor(carrier);
                            const rowBg = cidx % 2 === 1 ? '#fafafa' : 'white';
                            const isExcluded =
                                exclusionMap
                                    .get(carrier)
                                    ?.includes(line.crop) ?? false;
                            const discFrac = isExcluded
                                ? 0
                                : discountMap.get(carrier) ?? 0;
                            const chipInfo = isExcluded
                                ? undefined
                                : discountChipMap.get(carrier);

                            return (
                                <TableRow key={carrier}>
                                    {/* Carrier cell (sticky) */}
                                    <TableCell
                                        sx={{
                                            borderRight: '1px solid #e0e0e0',
                                            position: 'sticky',
                                            left: 0,
                                            bgcolor: rowBg,
                                            zIndex: 1,
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            gap={1}
                                            flexWrap="wrap"
                                        >
                                            <SelectionCircle
                                                selected={isCarrierSelected}
                                                onClick={() =>
                                                    onCarrierSelect(
                                                        lineIdx,
                                                        carrier
                                                    )
                                                }
                                            />
                                            <Chip
                                                label={carrierDisplayName(
                                                    carrier
                                                )}
                                                size="small"
                                                sx={{
                                                    borderRadius: '16px',
                                                    bgcolor: carrierColor.bg,
                                                    color: carrierColor.color,
                                                    fontWeight: 600,
                                                }}
                                            />
                                            {showDiscount && chipInfo && (
                                                <Chip
                                                    label={`${chipInfo.pct}${chipInfo.due ? ' | ' + chipInfo.due : ''}`}
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                    sx={{
                                                        fontSize: '0.65rem',
                                                        height: 20,
                                                    }}
                                                />
                                            )}
                                        </Stack>
                                    </TableCell>

                                    {/* Rate/Endorsements/Disc.Rate/Cost cells per product */}
                                    {products.map(product => {
                                        const rate = rateMap.get(
                                            `${carrier}|${product}`
                                        );
                                        const highlighted = isHighlighted(
                                            carrier,
                                            product
                                        );
                                        const cellBg = highlighted
                                            ? HIGHLIGHT_BG
                                            : rowBg;
                                        const cellWeight = highlighted
                                            ? 700
                                            : 400;
                                        // Zero out discount for excluded products
                                        const isProductExcluded =
                                            productExclusionMap
                                                .get(carrier)
                                                ?.includes(product) ?? false;
                                        const effectiveDiscFrac =
                                            isProductExcluded ? 0 : discFrac;

                                        // Sum endorsement rates for Cost/Acre.
                                        // The lookup is per (carrier, product,
                                        // endorsement, crop, county); old quotes
                                        // saved before the multi-crop fix
                                        // resolve via legacy-key fallback.
                                        let endorsementTotal = 0;
                                        const endoRates: (number | null)[] =
                                            endorsementEntries.map(endo => {
                                                const val =
                                                    readSavedEndorsementRate(
                                                        endorsementRateLookup,
                                                        carrier,
                                                        product,
                                                        endo.name,
                                                        line.crop ?? '',
                                                        line.county ?? ''
                                                    );
                                                if (val !== null)
                                                    endorsementTotal += val;
                                                return val;
                                            });

                                        // Effective rate = base rate + endorsement rates
                                        const baseNum = parseRateNum(
                                            rate?.selected_rate ?? null
                                        );
                                        const totalRate = isNaN(baseNum)
                                            ? null
                                            : baseNum + endorsementTotal;

                                        return (
                                            <React.Fragment key={product}>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        bgcolor: cellBg,
                                                        fontWeight: cellWeight,
                                                        fontSize: '0.85rem',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {rate
                                                        ? formatRate(
                                                              rate.selected_rate
                                                          )
                                                        : '—'}
                                                </TableCell>
                                                {endoRates.map(
                                                    (endoRate, ei) => (
                                                        <TableCell
                                                            key={
                                                                endorsementEntries[
                                                                    ei
                                                                ].type
                                                            }
                                                            align="right"
                                                            sx={{
                                                                bgcolor: cellBg,
                                                                fontWeight:
                                                                    cellWeight,
                                                                fontSize:
                                                                    '0.85rem',
                                                                whiteSpace:
                                                                    'nowrap',
                                                            }}
                                                        >
                                                            {endoRate !== null
                                                                ? endoRate.toFixed(
                                                                      2
                                                                  )
                                                                : '—'}
                                                        </TableCell>
                                                    )
                                                )}
                                                {showDiscount && (
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            bgcolor: cellBg,
                                                            fontWeight:
                                                                cellWeight,
                                                            fontSize: '0.85rem',
                                                            whiteSpace:
                                                                'nowrap',
                                                            color: '#1b8a6b',
                                                        }}
                                                    >
                                                        {rate
                                                            ? formatDiscRate(
                                                                  rate.selected_rate,
                                                                  effectiveDiscFrac
                                                              )
                                                            : '—'}
                                                    </TableCell>
                                                )}
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        bgcolor: cellBg,
                                                        fontWeight: cellWeight,
                                                        borderRight:
                                                            '1px solid #e0e0e0',
                                                        fontSize: '0.85rem',
                                                        whiteSpace: 'nowrap',
                                                        ...(showDiscount && {
                                                            color: '#1b8a6b',
                                                        }),
                                                    }}
                                                >
                                                    {totalRate !== null
                                                        ? showDiscount
                                                            ? formatDiscCostPerAcre(
                                                                  totalRate,
                                                                  line.ipa,
                                                                  effectiveDiscFrac
                                                              )
                                                            : formatCostPerAcre(
                                                                  totalRate,
                                                                  line.ipa
                                                              )
                                                        : '—'}
                                                </TableCell>
                                            </React.Fragment>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};
